/** Debug overlay drawing HP and aggro bars above each enemy sprite. */
import Phaser from 'phaser';
import { Enemy } from '../../entities/Enemy';
import { DebugConfig } from '../DebugConfig';

const HP_BAR_WIDTH = 24;
const HP_BAR_HEIGHT = 3;
const HP_BAR_OFFSET_Y = -14;
const HP_BG_COLOR = 0x333333;
const HP_FILL_COLOR = 0x00cc00;
const HP_LOW_COLOR = 0xcc0000;

const AGGRO_BAR_WIDTH = 24;
const AGGRO_BAR_HEIGHT = 2;
const AGGRO_BAR_OFFSET_Y = -10;
const AGGRO_BG_COLOR = 0x222222;
const AGGRO_HWM_COLOR = 0xffffff;

/** Draws HP and aggro bar overlays above each visible enemy. */
export class EnemyBarsOverlay {
  /** Draw all enabled bars (HP and/or aggro) for the given enemy list. */
  draw(graphics: Phaser.GameObjects.Graphics, enemies: readonly Enemy[]): void {
    if (DebugConfig.flags.hpBars) {
      this.drawHpBars(graphics, enemies);
    }
    if (DebugConfig.flags.aggroBars) {
      this.drawAggroBars(graphics, enemies);
    }
  }

  private drawHpBars(graphics: Phaser.GameObjects.Graphics, enemies: readonly Enemy[]): void {
    for (const enemy of enemies) {
      const x = enemy.sprite.x - HP_BAR_WIDTH / 2;
      const y = enemy.sprite.y + HP_BAR_OFFSET_Y;
      const ratio = enemy.healthComponent.ratio;

      graphics.fillStyle(HP_BG_COLOR, 0.8);
      graphics.fillRect(x, y, HP_BAR_WIDTH, HP_BAR_HEIGHT);

      const color = ratio > 0.3 ? HP_FILL_COLOR : HP_LOW_COLOR;
      graphics.fillStyle(color, 0.9);
      graphics.fillRect(x, y, HP_BAR_WIDTH * ratio, HP_BAR_HEIGHT);
    }
  }

  private drawAggroBars(graphics: Phaser.GameObjects.Graphics, enemies: readonly Enemy[]): void {
    for (const enemy of enemies) {
      const ai = enemy.ai;
      const aggro = ai.aggro;
      const hwm = ai.aggroHWM;

      if (aggro <= 0 && hwm <= 0) continue;

      const x = enemy.sprite.x - AGGRO_BAR_WIDTH / 2;
      const y = enemy.sprite.y + AGGRO_BAR_OFFSET_Y;

      graphics.fillStyle(AGGRO_BG_COLOR, 0.8);
      graphics.fillRect(x, y, AGGRO_BAR_WIDTH, AGGRO_BAR_HEIGHT);

      const ratio = aggro / 100;
      let color: number;
      if (ratio < 0.4) color = 0x00cc00;
      else if (ratio < 0.75) color = 0xcccc00;
      else color = 0xcc2222;

      graphics.fillStyle(color, 0.9);
      graphics.fillRect(x, y, AGGRO_BAR_WIDTH * ratio, AGGRO_BAR_HEIGHT);

      if (hwm > 0) {
        const hwmX = x + AGGRO_BAR_WIDTH * (hwm / 100);
        graphics.lineStyle(1, AGGRO_HWM_COLOR, 0.8);
        graphics.beginPath();
        graphics.moveTo(hwmX, y - 1);
        graphics.lineTo(hwmX, y + AGGRO_BAR_HEIGHT + 1);
        graphics.strokePath();
      }
    }
  }

  clear(): void { /* graphics-only, cleared by manager */ }
  destroy(): void { /* nothing to clean up */ }
}
